import { ToastrService } from 'ngx-toastr';
import { Injectable } from '@angular/core';
import { Store } from '../store';
import { ToolsService } from './tools.service';
import { Socket } from 'ngx-socket-io';
import { map, take } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { EndpointParams } from '../models/pagination-params';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { merge, of } from 'rxjs';
import { IChatOpened } from '../chat/chat.component';
import { EChatStoreKey } from '../chat/enums';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    query: any;
    sound;

    silencedNotices = false;
    private commonSocket: Socket;
    private adminSocket: Socket;
    private secretSocket: Socket;

    constructor(
        private http: ApiAuthHttpClient,
        private store: Store,
        private toolsService: ToolsService,
        private router: Router,
        private toastrService: ToastrService
    ) {
        this.silencedNotices = !!JSON.parse(localStorage.getItem('yss_notices_silenced'));

        this.initSound();
    }

    getNotifications(endpointParams: EndpointParams) {
        return this.http
            .post(env.apiPath + 'notifications/admin/search', endpointParams)
            .pipe(map((data: any) => data));
    }

    triggerNotices() {
        this.silencedNotices = !this.silencedNotices;
        localStorage.setItem('yss_notices_silenced', JSON.stringify(this.silencedNotices));
    }

    getUnfilteredNotifications() {
        const user = this.store.selectForLocal<any>('user');
        // No authenticated user (e.g. a delayed timer fires after logout / before
        // sign-in): reading user.id would throw "Cannot read properties of undefined".
        if (!user) {
            return of(null);
        }

        return this.http.get(`${env.apiPath}notifications/admin/${user.id}`).pipe(
            map((data: any) => {
                this.store.set('notifications', data.data);
                return data;
            })
        );
    }

    delete(id) {
        return this.http.delete(env.apiPath + 'notifications/' + id).pipe(map((data: any) => data));
    }

    markAsRead(id) {
        const dataToSend = {
            id,
        };
        return this.http.put(env.apiPath + 'notifications/' + id, dataToSend).pipe(map((data: any) => data));
    }

    listenToNotifications(userData: any) {
        const domain = this.store.selectForLocal('domain');
        this.commonSocket = new Socket({ url: `${env.socketPath}` }); // dashboard chanel
        this.adminSocket = new Socket({ url: `${env.socketPath}${domain}-admin` }); // dashboard chanel
        this.secretSocket = new Socket({ url: `${env.socketPath}${domain}-${userData.id}` }); // user specific channel

        this.adminSocket
            .fromEvent('order-line-update')
            .subscribe((orderLine: any) => this.store.set('updatedOrderLine', orderLine));

        this.adminSocket
            .fromEvent('order-board-update')
            .subscribe((orderLine: any) => this.store.set('updatedOrderBoard', orderLine));

        merge(
            this.commonSocket.fromEvent('notifications'),
            this.secretSocket.fromEvent('notifications'),
            this.adminSocket.fromEvent('notifications')
        ).subscribe((data: any) => {
            const notifications = this.store.selectForLocal('notifications');

            notifications.unread++;
            notifications.data.unshift(data);

            const changeTrigger = this.store.selectForLocal('changeTrigger');
            changeTrigger.orders++;
            changeTrigger.samples++;
            this.store.set('changeTrigger', changeTrigger);
            this.store.set('notifications', notifications);

            this.showNotificationToast(data);
        });
    }

    stopListening() {
        this.store.set('notifications', { unread: 0, data: [] });
        this.adminSocket.disconnect();
        this.secretSocket.disconnect();
    }

    send(notificationData) {
        const newId = this.toolsService.newUUID();
        notificationData.id = newId;
        notificationData.read = false;
        notificationData.deleted = false;
        notificationData.createdAt = new Date();
    }

    initSound() {
        this.sound = document.createElement('audio');
        this.sound.src = '/assets/ding.mp3';
        this.sound.setAttribute('preload', 'auto');
        this.sound.setAttribute('controls', 'none');
        this.sound.style.display = 'none';
        document.body.appendChild(this.sound);
    }

    markAllAsRead(id = null) {
        if (id) {
            return this.http.put(env.apiPath + 'notifications/admin/user/' + id, {}).pipe(map((data: any) => data));
        }
    }

    deleteAll(id = null) {
        if (id) {
            return this.http.delete(env.apiPath + 'notifications/admin/user/' + id).pipe(map((data: any) => data));
        } else {
            return this.http.delete(env.apiPath + 'notifications/admin/user').pipe(map((data: any) => data));
        }
    }

    private showNotificationToast(data) {
        const rawTxt = data.content.replace(/<[^>]*>?/gm, '');

        if (data.type === 'chatMessage' || data.type === 'chatFile') {
            // no need to show notification messages sent in opened chat
            const openedChat: IChatOpened = this.store.selectForLocal(EChatStoreKey.OpenedChat);
            if (openedChat?.id === data.messageGroupId) return;

            const toastRef = this.toastrService.success(rawTxt, data.subject);
            if (!this.silencedNotices) {
                this.sound.play();
            }
            toastRef.onTap.pipe(take(1)).subscribe(() => {
                this.router.navigate([`/chat/${data.messageGroupId}/`]);
            });

            return;
        }
        this.toastrService.success(rawTxt, data.subject);
        if (!this.silencedNotices) {
            this.sound.play();
        }
    }
}
